import { isEqual } from 'lodash';

import { ILocation } from '../../../api/locations';
import { IAppState, ITagsData, TActions } from '../../../common/state';
import { IJsonQuery } from '../../../json_query';
import { accessSystemTagDefinition, IAccessSystemTag } from './definitions/access_system';
import { agentCallsForbiddenTagDefinition, IAgentCallsForbiddenTag } from './definitions/agent_calls_forbidden';
import { apartmentTagDefinition, IApartmentTag } from './definitions/apartment';
import { balconyTagDefinition, IBalconyTag } from './definitions/balcony';
import { bedroomsMinCountTagDefinition, IBedroomsMinCountTag } from './definitions/bedrooms_min_count';
import { bsCenterTagDefinition, IBSCenterTag } from './definitions/bs_center';
import { buildingClassTypesTagDefinition, IBuildingClassTypesTag } from './definitions/building_class';
import {
  IWarehouseBuildingClassTypesTag,
  warehouseBuildingClassTypesTagDefinition,
} from './definitions/building_class_warehouse';
import { buildingConditionTypesTagDefinition, IBuildingConditionsTag } from './definitions/building_condition';
import { buildingCraneTypesTagDefinition, IBuildingCraneTypesTag } from './definitions/building_crane';
import { buildingElevatorTypesTagDefinition, IBuildingElevatorTypesTag } from './definitions/building_elevator';
import { buildingGatesTypesTagDefinition, IBuildingGatesTypesTag } from './definitions/building_gates';
import { buildingHeatingTypesTagDefinition, IBuildingHeatingTypesTag } from './definitions/building_heating';
import { buildingTypeTagDefinition, IBuildingTypeTag } from './definitions/building_type';
import { buildingTypeBuildingTagDefinition, IBuildingTypeBuildingTag } from './definitions/building_type.building';
import { ceilingHeightTagDefinition, ICeilingHeightTag } from './definitions/ceiling_height';
import { comissionTypeTagDefinition, IComissionTypeTag } from './definitions/comission_type';
import { commercialSpecialtyTagDefinition, ICommercialSpecialtyTag } from './definitions/commercial_specialty';
import { contractTypeTagDefinition, IContractTypeTag } from './definitions/contract';
import { decorationTagDefinition, IDecorationTag } from './definitions/decoration';
import {
  demolishedInMoscowProgrammTagDefinition,
  IDemolishedInMoscowProgrammTag,
} from './definitions/demolished_in_moscow_programm';
import { developerTagDefinition, IDeveloperTag } from './definitions/developer';
import { domrfTagDefinition, IDomrfTag } from './definitions/domrf';
import { elevatorsHasServiceTagDefinition, IElevatorsHasServiceTag } from './definitions/elevators_has_service';
import { elevatorsMinCountTagDefinition, IElevatorsMinCountTag } from './definitions/elevators_min_count';
import { entranceTypesTagDefinition, IEntranceTypesTag } from './definitions/entrance';
import { estateTypeTagDefinition, IEstateTypeTag } from './definitions/estate_type';
import { facilityTagDefinition, IFacilityTag } from './definitions/facilities';
import { floorTagDefinition, IFloorTag } from './definitions/floor';
import { furnitureTagDefinition, IFurnitureTag } from './definitions/furniture';
import { garageKindsTagDefinition, IGarageKindsTag } from './definitions/garage_kind';
import { garageMaterialsTagDefinition, IGarageMaterialsTag } from './definitions/garage_material';
import { garageStatusesTagDefinition, IGarageStatusesTag } from './definitions/garage_status';
import { garageTypesTagDefinition, IGarageTypesTag } from './definitions/garage_type';
import {
  geoCircleTagDefinition,
  geoDeveloperTagDefinition,
  geoDistrictTagDefinition,
  geoHighwayTagDefinition,
  geoHouseTagDefinition,
  geoLocationTagDefinition,
  geoNewbuildingTagDefinition,
  geoPolygonTagDefinition,
  geoBboxTagDefinition,
  geoStreetTagDefinition,
  geoUndergroundTagDefinition,
  geoRailwayTagDefinition,
  TGeoTag,
} from './definitions/geo';
import { hasElectricityTagDefinition, IHasElectricityTag } from './definitions/has_electricity';
import { hasEquipmentTagDefinition, IHasEquipmentTag } from './definitions/has_equipment';
import {
  hasExtinguishingSystemTagDefinition,
  IHasExtinguishingSystemTag,
} from './definitions/has_extinguishing_system';
import { hasHeatingTagDefinition, IHasHeatingTag } from './definitions/has_heating';
import { IHasLightTag } from './definitions/has_light';
import { hasWaterTagDefinition, IHasWaterTag } from './definitions/has_water';
import { heatingTypesTagDefinition, IHeatingTypesTag } from './definitions/heating';
import { hiddenBaseTagDefinition, IHiddenBaseTag } from './definitions/hidden_base';
import { houseLineBuildingTag, IHouseLineBuildingTag } from './definitions/house_line.building';
import { houseLineShoppingAreaTagDefinition, IHouseLineShoppingAreaTag } from './definitions/house_line.shopping_area';
import { houseMaterialTagDefinition, IHouseMaterialTag } from './definitions/house_material';
import { houseYearTagDefinition, IHouseYearTag } from './definitions/house_year';
import { IInfrastructureParamTag, infrastructureParamsTagDefinition } from './definitions/infrastructure';
import { IIsBasementFloorTag, isBasementFloorTagDefinition } from './definitions/is_basement_floor';
import { IByHomeOwnerTag, isByHomeOwnerTagDefinition } from './definitions/is_by_homeowner';
import { IIsFirstFloorTag, isFirstFloorTagDefinition } from './definitions/is_first_floor';
import { IIsSemibasementFloorTag, isSemibasementFloorTagDefinition } from './definitions/is_semibasement_floor';
import { IKitchenAreaTag, kitchenAreaTagDefinition } from './definitions/kitchen_area';
import { IKpIdTag, kpIdTagDefinition } from './definitions/kp_id';
import { ILandStatusTypesTag, landStatusTypesTagDefinition } from './definitions/land_status';
import { ILayoutTypeTag, layoutTypeTagDefinition } from './definitions/layout';
import { ILivingAreaTag, livingAreaTagDefinition } from './definitions/living_area';
import { ILoggiaTag, loggiaTagDefinition } from './definitions/loggia';
import { IManufactureConditionsTag, manufactureConditionTypesTagDefinition } from './definitions/manufacture_condition';
import { IMaxPrepayTag, maxPrepayTagDefinition } from './definitions/max_prepay';
import { IMortgagePossibleTag, mortgagePossibleTagDefinition } from './definitions/mortgage_possible';
import { IMultiIdTag, multiIdTagDefinition } from './definitions/multi_id';
import { IIncludeNewMoscowTag, includeNewMoscowTagDefinition } from './definitions/new_moscow';
import { INewbuildingCompletedTag, newbuildingCompletedTagDefinition } from './definitions/newbuilding_completed';
import {
  buildingDoneYearsTagDefinition,
  INewbuildingCompletionAfterTag,
} from './definitions/newbuilding_completion_after';
import {
  INewbuildingCompletionYearTag,
  newbuildingCompletionYearTagDefinition,
} from './definitions/newbuilding_completion_year';
import { INotLastFloorTag, notLastFloorTagDefinition } from './definitions/not_last_floor';
import { IOfferIdTag, offerIdTagDefinition } from './definitions/offer_id';
import { IOfficeConditionsTag, officeConditionTypesTagDefinition } from './definitions/office_condition';
import { IParkingTypesTag, parkingTypesTagDefinition } from './definitions/parking';
import { IPenthouseTag, penthouseTagDefinition } from './definitions/penthouse';
import { IPhoneTag, phoneTagDefinition } from './definitions/phone';
import { IPlacementTypeTag, placementTypeTagDefinition } from './definitions/placement_type';
import { IPromoTag, promoTagDefinition } from './definitions/promo';
import { IPropertyTypeTag, propertyTypeTagDefinition } from './definitions/property_type';
import { IPublishPeriodTag, publishPeriodTagDefinition } from './definitions/publish_period';
import { IRentTimeTag, rentTimeTagDefinition } from './definitions/rent_time';
import { IRepairTag, repairTagDefinition } from './definitions/repair';
import { IRoomAreaTag, roomAreaTagDefinition } from './definitions/room_area';
import { IRoomsInDealTag, roomsInDealTagDefinition } from './definitions/rooms_in_deal';
import { IRoomsTotalTag, roomsTotalTagDefinition } from './definitions/rooms_total';
import { ISaleTypesTag, saleTypesTagDefinition } from './definitions/sale_type';
import { ISiteAreaTag, siteAreaTagDefinition } from './definitions/site_area';
import { ISuburbanUtilityTag, suburbanUtilityTagDefinition } from './definitions/suburban_utilities';
import { ISuburbanWCTypeTag, suburbanWCTypeTagDefinition } from './definitions/suburban_wc';
import { ITenantsTag, tenantsTagDefinition } from './definitions/tenants';
import { ITotalAreaTag, totalAreaTagDefinition } from './definitions/total_area';
import { ITotalFloorsTag, totalFloorsTagDefinition } from './definitions/total_floors';
import { IUserIdTag, userIdTagDefinition } from './definitions/user_id';
import { hasVideoTagDefinition, IHasVideoTag } from './definitions/video';
import { IWCCountTag, wcCountTagDefinition } from './definitions/wc_count';
import { IWCTypeTag, wcTypeTagDefinition } from './definitions/wc_type';
import { IWindowsTypeTag, windowsTagDefinition } from './definitions/windows';
import { IWithPhotoTag, withPhotoTagDefinition } from './definitions/with_photo';
import { IWithoutDepositTag, withoutDepositTagDefinition } from './definitions/without_deposit';
import { IWordsExcludeTag, wordsExcludeTagDefinition } from './definitions/words_exclude';
import { IWordsIncludeTag, wordsIncludeTagDefinition } from './definitions/words_include';
import { IExperiment } from '../../../api/experiments';
import { IWithLayoutTag, withLayoutTagDefinition } from './definitions/with_layout';
import { IIdenticalOffersIdTag, identicalOffersIdTagDefinition } from './definitions/identical_offers_id';
import { IDealRentTag, isDealRentTagDefinition } from './definitions/is_deal_rent';
import { amenityTagDefinition, IAmenityTag } from './definitions/amenity';
import { coworkingAccessTagDefinition, ICoworkinAccessTag } from './definitions/coworking_access';
import { INewbuildingHouseTag, geoNewbuildingHouseTagDefinition } from './definitions/geo/newbuilding_houses';
import { coworkingIDTagDefinition, ICoworkingIdTag } from './definitions/coworking_id';

// tslint:disable:max-file-line-count
export interface ITagDefinition {
  reduce(
    tags: TTag[],
    jsonQuery: IJsonQuery,
    tagsData: ITagsData,
    currentLocation: ILocation | 'moscow_mo' | 'spb_lo',
    experiments: IExperiment[],
    isFiltersRedesign: boolean,
  ): TTag[];
}

export type TMultiSelectTag =
  | IBuildingClassTypesTag
  | IBuildingConditionsTag
  | IBuildingCraneTypesTag
  | IBuildingElevatorTypesTag
  | IBuildingGatesTypesTag
  | IBuildingHeatingTypesTag
  | IContractTypeTag
  | ICommercialSpecialtyTag
  | IEntranceTypesTag
  | IEstateTypeTag
  | IGarageKindsTag
  | IGarageMaterialsTag
  | IGarageStatusesTag
  | IGarageTypesTag
  | IHouseLineBuildingTag
  | IHouseLineShoppingAreaTag
  | IManufactureConditionsTag
  | IPlacementTypeTag
  | IOfficeConditionsTag
  | IWarehouseBuildingClassTypesTag;

export type TRadioSelectTag = IAccessSystemTag;

export type TTag =
  | TMultiSelectTag
  | TRadioSelectTag
  | IInfrastructureParamTag
  | IAgentCallsForbiddenTag
  | IApartmentTag
  | IBSCenterTag
  | IBalconyTag
  | IBedroomsMinCountTag
  | IBuildingTypeBuildingTag
  | IBuildingTypeTag
  | IByHomeOwnerTag
  | ICeilingHeightTag
  | IComissionTypeTag
  | IDecorationTag
  | IDemolishedInMoscowProgrammTag
  | IDeveloperTag
  | IDomrfTag
  | IElevatorsHasServiceTag
  | IElevatorsMinCountTag
  | IFacilityTag
  | IFloorTag
  | IFurnitureTag
  | IHasElectricityTag
  | IHasEquipmentTag
  | IHasExtinguishingSystemTag
  | IHasHeatingTag
  | IHasLightTag
  | IHasVideoTag
  | IHasWaterTag
  | IHeatingTypesTag
  | IHiddenBaseTag
  | IHouseMaterialTag
  | IHouseYearTag
  | IIncludeNewMoscowTag
  | IIsBasementFloorTag
  | IIsFirstFloorTag
  | IIsSemibasementFloorTag
  | IKitchenAreaTag
  | ILandStatusTypesTag
  | ILayoutTypeTag
  | ILivingAreaTag
  | ILoggiaTag
  | IMaxPrepayTag
  | IMortgagePossibleTag
  | IMultiIdTag
  | INewbuildingCompletedTag
  | INewbuildingCompletionAfterTag
  | INewbuildingCompletionYearTag
  | INewbuildingHouseTag
  | INotLastFloorTag
  | IOfferIdTag
  | IParkingTypesTag
  | IPenthouseTag
  | IPhoneTag
  | IPromoTag
  | IPropertyTypeTag
  | IPublishPeriodTag
  | IRentTimeTag
  | IRepairTag
  | IRoomAreaTag
  | IRoomsInDealTag
  | IRoomsTotalTag
  | ISaleTypesTag
  | ISiteAreaTag
  | ISuburbanUtilityTag
  | ISuburbanWCTypeTag
  | ITenantsTag
  | ITotalAreaTag
  | ITotalFloorsTag
  | IUserIdTag
  | IWCCountTag
  | IWCTypeTag
  | IWindowsTypeTag
  | IWithPhotoTag
  | IWithLayoutTag
  | IWithoutDepositTag
  | IWordsExcludeTag
  | IWordsIncludeTag
  | TGeoTag
  | IKpIdTag
  | IIdenticalOffersIdTag
  | IDealRentTag
  | IAmenityTag
  | ICoworkinAccessTag
  | ICoworkingIdTag;

export interface ITagRemovedAction {
  type: 'filters/tags/TAG_REMOVED';
  tag: TTag;
  index: number;
}

export interface IAllTagsRemovedAction {
  type: 'filters/tags/ALL_TAGS_REMOVED';
}

// tslint:disable-next-line:no-any
export const tagDefinitions: ITagDefinition[] = [
  ...facilityTagDefinition,
  ...infrastructureParamsTagDefinition,
  ...suburbanUtilityTagDefinition,
  accessSystemTagDefinition,
  agentCallsForbiddenTagDefinition,
  apartmentTagDefinition,
  balconyTagDefinition,
  bedroomsMinCountTagDefinition,
  bsCenterTagDefinition,
  buildingClassTypesTagDefinition,
  buildingConditionTypesTagDefinition,
  buildingCraneTypesTagDefinition,
  buildingDoneYearsTagDefinition,
  buildingElevatorTypesTagDefinition,
  buildingGatesTypesTagDefinition,
  buildingHeatingTypesTagDefinition,
  buildingTypeBuildingTagDefinition,
  buildingTypeTagDefinition,
  ceilingHeightTagDefinition,
  comissionTypeTagDefinition,
  commercialSpecialtyTagDefinition,
  contractTypeTagDefinition,
  decorationTagDefinition,
  demolishedInMoscowProgrammTagDefinition,
  developerTagDefinition,
  domrfTagDefinition,
  elevatorsHasServiceTagDefinition,
  elevatorsMinCountTagDefinition,
  entranceTypesTagDefinition,
  estateTypeTagDefinition,
  floorTagDefinition,
  furnitureTagDefinition,
  garageKindsTagDefinition,
  garageMaterialsTagDefinition,
  garageStatusesTagDefinition,
  garageTypesTagDefinition,
  geoCircleTagDefinition,
  geoDeveloperTagDefinition,
  geoDistrictTagDefinition,
  geoHighwayTagDefinition,
  geoHouseTagDefinition,
  geoLocationTagDefinition,
  geoNewbuildingTagDefinition,
  geoPolygonTagDefinition,
  geoBboxTagDefinition,
  geoStreetTagDefinition,
  geoUndergroundTagDefinition,
  geoRailwayTagDefinition,
  geoNewbuildingHouseTagDefinition,
  hasElectricityTagDefinition,
  hasEquipmentTagDefinition,
  hasExtinguishingSystemTagDefinition,
  hasHeatingTagDefinition,
  hasVideoTagDefinition,
  hasWaterTagDefinition,
  heatingTypesTagDefinition,
  hiddenBaseTagDefinition,
  houseLineBuildingTag,
  houseLineShoppingAreaTagDefinition,
  houseMaterialTagDefinition,
  houseYearTagDefinition,
  includeNewMoscowTagDefinition,
  isBasementFloorTagDefinition,
  isByHomeOwnerTagDefinition,
  isFirstFloorTagDefinition,
  isSemibasementFloorTagDefinition,
  kitchenAreaTagDefinition,
  kpIdTagDefinition,
  landStatusTypesTagDefinition,
  layoutTypeTagDefinition,
  livingAreaTagDefinition,
  loggiaTagDefinition,
  manufactureConditionTypesTagDefinition,
  maxPrepayTagDefinition,
  mortgagePossibleTagDefinition,
  multiIdTagDefinition,
  newbuildingCompletedTagDefinition,
  newbuildingCompletionYearTagDefinition,
  notLastFloorTagDefinition,
  offerIdTagDefinition,
  officeConditionTypesTagDefinition,
  parkingTypesTagDefinition,
  penthouseTagDefinition,
  phoneTagDefinition,
  placementTypeTagDefinition,
  promoTagDefinition,
  propertyTypeTagDefinition,
  publishPeriodTagDefinition,
  rentTimeTagDefinition,
  repairTagDefinition,
  roomAreaTagDefinition,
  roomsInDealTagDefinition,
  roomsTotalTagDefinition,
  saleTypesTagDefinition,
  siteAreaTagDefinition,
  suburbanWCTypeTagDefinition,
  tenantsTagDefinition,
  totalAreaTagDefinition,
  totalFloorsTagDefinition,
  userIdTagDefinition,
  warehouseBuildingClassTypesTagDefinition,
  wcCountTagDefinition,
  wcTypeTagDefinition,
  windowsTagDefinition,
  withPhotoTagDefinition,
  withLayoutTagDefinition,
  withoutDepositTagDefinition,
  wordsExcludeTagDefinition,
  wordsIncludeTagDefinition,
  identicalOffersIdTagDefinition,
  isDealRentTagDefinition,
  amenityTagDefinition,
  coworkingAccessTagDefinition,
  coworkingIDTagDefinition,
];

export const restoreTagsFromJsonQuery = (
  jsonQuery: IJsonQuery,
  tagsData: ITagsData,
  location: ILocation | 'moscow_mo' | 'spb_lo',
  experiments: IExperiment[],
  isFiltersRedesign: boolean,
): TTag[] =>
  tagDefinitions.reduce((acc, tagDefinition) => {
    return tagDefinition.reduce(acc, jsonQuery, tagsData, location, experiments, isFiltersRedesign);
  }, [] as TTag[]);

export function tagsReducer(state: IAppState, action: TActions): IAppState {
  const tags: TTag[] = tagDefinitions.reduce((acc, tagDefinition) => {
    return tagDefinition.reduce(
      acc,
      state.filters.jsonQuery,
      state.filters.tagsData,
      state.filters.currentLocation,
      state.experiments,
      state.isFiltersRedesignEnabled,
    );
  }, state.filters.tags);

  if (!isEqual(state.filters.tags, tags)) {
    return {
      ...state,
      filters: {
        ...state.filters,
        tags,
      },
    };
  }

  return state;
}
